import { BetSlipItem } from './types';

/**
 * Detects if the user is on an actual mobile device (not just a mobile viewport on desktop)
 * This is important for deep links since mobile app schemes won't work on desktop browsers
 */
export const isActualMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent || '';

  // Check for actual mobile OS in user agent (not just "mobile" keyword which can be ambiguous)
  const isMobileOS = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini|webos/i.test(userAgent);

  // Check if device has touch capability (real mobile devices have this)
  const hasTouchScreen = 'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0;

  // Must be both a mobile OS AND have touch capability
  // This prevents desktop browsers in mobile viewport from being detected as mobile
  const isMobile = isMobileOS && hasTouchScreen;

  console.log('[DeepLink] Device detection:', { userAgent: userAgent.substring(0, 80), isMobileOS, hasTouchScreen, isMobile });

  return isMobile;
};

export const getPlatformDeepLink = (bets: BetSlipItem[], isMobile: boolean = false) => {
  try {
    if (!bets?.length) {
      return '';
    }

    if (isMobile) {
      return getMobileDeepLink(bets);
    }

    return getParlayDeepLink(bets);
  } catch (e) {
    console.log('getPlatformDeepLink error', e);
    return null;
  }
};

export const getMobileDeepLink = (bets: BetSlipItem[]) => {
  if (!bets?.length) {
    return null;
  }

  const sportsbookCode = bets[0]?.book?.toLowerCase();
  let result: string | null = null;

  switch (sportsbookCode) {
    case 'draftkings':
      result = DraftKingsMobileLink(bets);
      break;
    case 'caesars':
      result = CaesarsMobileLink(bets, null);
      break;
    case 'betmgm':
      result = BetMGMMobileLink(bets, null);
      break;
    case 'fanduel':
      result = FanduelMobileLink(bets);
      // Fallback: convert any FanDuel web URL to the app scheme
      if (!result && bets[0]?.deepLinkUrl?.includes('fanduel.com')) {
        const url = bets[0].deepLinkUrl;
        const qsIndex = url.indexOf('?');
        const qs = qsIndex >= 0 ? url.substring(qsIndex) : '';
        const path = qsIndex >= 0 ? url.substring(0, qsIndex) : url;
        const pathOnly = path.replace(/^https?:\/\/[^/]+/, '');
        result = `fanduelsportsbook://account.sportsbook.fanduel.com/sportsbook${pathOnly}${qs}`;
      }
      break;
    case 'prizepicks':
      result = PrizePicksParlayLink(bets);
      break;
    case 'hardrock':
    case 'hardrocknj':
      result = HardRockMobileLink(bets);
      break;
    case 'espnbet':
      result = ESPNBetMobileLink(bets);
      break;
    case 'fanatics':
      result = FanaticsParlayLink(bets);
      break;
    default:
      // For unknown sportsbooks, just use the first bet's deepLinkUrl
      result = bets[0]?.deepLinkUrl || null;
      break;
  }

  // Fallback to first bet's deepLinkUrl if parlay link failed
  return result || bets[0]?.deepLinkUrl || null;
};

const FanduelMobileLink = (bets: BetSlipItem[]) => {
  if (!bets?.length || bets.filter(bet => bet.deepLinkUrl?.includes('marketId[0]=')).length === 0) {
    return null;
  }
  const filteredBets = bets.filter(bet => bet.deepLinkUrl?.includes('marketId[0]='));
  let url = "fanduelsportsbook://account.sportsbook.fanduel.com/sportsbook/addToBetslip?";
  const combinedParams = new URLSearchParams();
  let counter = 0;
  filteredBets.forEach(bet => {
    new URLSearchParams(bet.deepLinkUrl?.split('?')[1]).forEach((value, key) => {
      const match = key.match(/\[(\d+)\]$/);
      const newKey = match ? key.replace(/\[\d+\]$/, `[${+match[1] + counter}]`) : key;
      combinedParams.set(newKey, value);
    });
    counter++;
  });
  return `${url}${decodeURIComponent(combinedParams.toString())}`;
};

const CaesarsMobileLink = (bets: BetSlipItem[], stake: number | null) => {
  if (!bets?.length || bets.filter(bet => bet.deepLinkUrl?.includes('selectionIds=')).length === 0) {
    return null;
  }
  let url = "williamhillus://bet/betslip?selectionids=";
  bets.forEach(bet => {
    let selectionId = bet.deepLinkUrl?.split('selectionIds=')[1];
    url += selectionId + ",";
  });
  url = url.slice(0, -1);
  if (stake) {
    url += "&stake=" + stake * 100;
  }
  return url;
};

const BetMGMMobileLink = (bets: BetSlipItem[], stake: number | null) => {
  if (!bets?.length || bets.filter(bet => bet.deepLinkUrl?.includes('events')).length === 0) {
    return null;
  }
  let parlayUrl = BetMGMParlayLink(bets);
  if (!parlayUrl) {
    return null;
  }
  parlayUrl = parlayUrl.replace(/\/events\/\d+\//g, "");
  parlayUrl = parlayUrl.replace(/\.[a-z]{2}\./gi, '.');
  let mobileUrl = "playmgmsportswrp://navigation?scheme=https&url=" + parlayUrl?.split("https://")[1];
  return `https://playmgmsports.onelink.me/TkMx?${encodeURIComponent(parlayUrl)}af_web_dp=&af_dp=${encodeURIComponent(mobileUrl)}`;
};

const DraftKingsMobileLink = (bets: BetSlipItem[]) => {
  if (!bets?.length) {
    return null;
  }
  const parlayUrl = DraftKingsParlayLink(bets);
  if (!parlayUrl) {
    return null;
  }
  let allOutcomes = parlayUrl?.split('?outcomes=')[1];
  allOutcomes = allOutcomes.replaceAll(" | ", "+");
  allOutcomes = allOutcomes.replaceAll("|", "+");
  return 'dksb://sb/addbet/' + allOutcomes;
};

const HardRockMobileLink = (bets: BetSlipItem[]) => {
  // Custom URL scheme works on iOS/Android: hardrock://betslip/[outcomeId]
  if (!bets?.length) {
    return null;
  }
  const outcomeIds = bets.map(bet => {
    const match = bet?.deepLinkUrl?.match(/betslip%2F(\d+)/);
    return match ? match[1] : null;
  })
    .filter(id => id !== null)
    .join(",");

  if (!outcomeIds) {
    return null;
  }

  return `hardrock://betslip/${outcomeIds}`;
};

const createPENNMobileLink = (baseUrl: string, bets: BetSlipItem[]): string | null => {
  if (!bets?.length || bets.filter(bet => bet.deepLinkUrl?.includes('?')).length === 0) {
    return null;
  }
  const filteredBets = bets.filter(bet => bet.deepLinkUrl?.includes('?'));
  const combinedParams = new URLSearchParams();
  let counter = 0;
  filteredBets.forEach(bet => {
    new URLSearchParams(bet.deepLinkUrl?.split('?')[1]).forEach((value, key) => {
      const match = key.match(/\[(\d+)\]$/);
      const newKey = match ? key.replace(/\[\d+\]$/, `[${+match[1] + counter}]`) : key;
      combinedParams.set(newKey, value);
    });
    counter++;
  });
  return `${baseUrl}/?${decodeURIComponent(combinedParams.toString())}`;
};

export const ESPNBetMobileLink = (bets: BetSlipItem[]) =>
  createPENNMobileLink("https://espnbet.app.link", bets);

export const getParlayDeepLink = (bets: BetSlipItem[]) => {
  if (!bets?.length) {
    return null;
  }
  const sportsbookCode = bets[0]?.book?.toLowerCase();
  let result: string | null = null;

  switch (sportsbookCode) {
    case 'prizepicks':
      result = PrizePicksParlayLink(bets);
      break;
    case 'draftkings':
      result = DraftKingsParlayLink(bets);
      break;
    case 'caesars':
      result = CaesarsParlayLink(bets);
      break;
    case 'betmgm':
      result = BetMGMParlayLink(bets);
      break;
    case 'betrivers':
    case 'sugarhouse':
      result = KambiParlayLink(bets);
      break;
    case 'bet365':
      result = Bet365ParlayLink(bets);
      break;
    case 'fanduel':
      result = FanDuelParlayLink(bets);
      break;
    case 'hardrock':
    case 'hardrocknj':
      result = HardRockParlayLink(bets);
      break;
    case 'espnbet':
      result = ESPNBetParlayLink(bets);
      break;
    case 'fanatics':
      result = FanaticsParlayLink(bets);
      break;
    default:
      // For unknown sportsbooks, just use the first bet's deepLinkUrl
      result = bets[0]?.deepLinkUrl || null;
      break;
  }

  // Fallback to first bet's deepLinkUrl if parlay link failed
  return result || bets[0]?.deepLinkUrl || null;
};

const FanaticsParlayLink = (bets: BetSlipItem[]) => {
  if (!bets?.length || bets.filter(bet => bet.deepLinkUrl?.includes('?')).length === 0) {
    return null;
  }
  const combinedLegs: any[] = [];

  bets.map(bet => bet.deepLinkUrl).forEach((url) => {
    if (!url) return;
    try {
      const urlParams = new URL(url).searchParams;
      const deepLinkSub1 = urlParams.get("deep_link_sub1");
      if (deepLinkSub1) {
        const decodedJson = decodeURIComponent(deepLinkSub1);
        const legsObject: any = JSON.parse(decodedJson);
        if (legsObject?.legs) {
          combinedLegs.push(...legsObject.legs);
        }
      }
    } catch (e) {
      // Skip invalid URLs
    }
  });

  if (!combinedLegs.length) {
    return bets[0]?.deepLinkUrl || null;
  }

  const combinedBetSlip: any = { legs: combinedLegs };
  const encodedJson = encodeURIComponent(JSON.stringify(combinedBetSlip));
  return `https://fanatics.onelink.me/5kut/?af_force_deeplink=true&deep_link_sub1=${encodedJson}&source_caller=sdk&pid=share_bet&deep_link_value=consume-betslip`;
};

const HardRockParlayLink = (bets: BetSlipItem[]) => {
  if (!bets?.length) {
    return null;
  }
  const outcomeIds = bets.map(bet => {
    const match = bet?.deepLinkUrl?.match(/betslip%2F(\d+)/);
    return match ? match[1] : null;
  })
    .filter(id => id !== null)
    .join(",");
  const parlayUrl = bets[0].deepLinkUrl?.split("betslip%2F")[0] + "betslip%2F" + outcomeIds;
  return parlayUrl;
};

const ESPNBetParlayLink = (bets: BetSlipItem[]) => {
  if (!bets?.length || bets.filter(bet => bet.deepLinkUrl?.includes('?')).length === 0) {
    return null;
  }
  const filteredBets = bets.filter(bet => bet.deepLinkUrl?.includes('?'));
  const combinedParams = new URLSearchParams();
  let counter = 0;
  filteredBets.forEach(bet => {
    new URLSearchParams(bet.deepLinkUrl?.split('?')[1]).forEach((value, key) => {
      const match = key.match(/\[(\d+)\]$/);
      const newKey = match ? key.replace(/\[\d+\]$/, `[${+match[1] + counter}]`) : key;
      combinedParams.set(newKey, value);
    });
    counter++;
  });
  return `${filteredBets[0].deepLinkUrl?.split('?')[0]}?${decodeURIComponent(combinedParams.toString())}`;
};

const FanDuelParlayLink = (bets: BetSlipItem[]) => {
  if (!bets?.length || bets.filter(bet => bet.deepLinkUrl?.includes('?')).length === 0) {
    return null;
  }
  const filteredBets = bets.filter(bet => bet.deepLinkUrl?.includes('?'));
  const combinedParams = new URLSearchParams();
  let counter = 0;
  filteredBets.forEach(bet => {
    new URLSearchParams(bet.deepLinkUrl?.split('?')[1]).forEach((value, key) => {
      const match = key.match(/\[(\d+)\]$/);
      const newKey = match ? key.replace(/\[\d+\]$/, `[${+match[1] + counter}]`) : key;
      combinedParams.set(newKey, value);
    });
    counter++;
  });
  return `${filteredBets[0].deepLinkUrl?.split('?')[0]}?${decodeURIComponent(combinedParams.toString())}`;
};

const PrizePicksParlayLink = (bets: BetSlipItem[]) => {
  if (!bets?.length || bets.filter(bet => bet.deepLinkUrl?.includes('&projections=')).length === 0) {
    return null;
  }
  const filteredBets = bets.filter(bet => bet.deepLinkUrl?.includes('&projections='));
  let url = filteredBets[0].deepLinkUrl?.split('&projections=')[0] + '&projections=';
  const outcomeIds: string[] = [];
  filteredBets.forEach((bet) => {
    let outcomeId = bet.deepLinkUrl?.split('&projections=')[1];
    if (outcomeId) outcomeIds.push(outcomeId);
  });
  return url + outcomeIds.join(',');
};

const DraftKingsParlayLink = (bets: BetSlipItem[]) => {
  if (!bets?.length || bets.filter(bet => bet.deepLinkUrl?.includes('?outcomes=')).length === 0) {
    return null;
  }
  const filteredBets = bets.filter(bet => bet.deepLinkUrl?.includes('?outcomes='));
  let url = filteredBets[0].deepLinkUrl?.split('?outcomes=')[0] + '?outcomes=';
  const outcomeIds: string[] = [];
  filteredBets.forEach((bet) => {
    let firstParse = bet.deepLinkUrl?.split('?outcomes=')[1];
    let outcomeId = firstParse?.split('&')[0];
    if (outcomeId) outcomeIds.push(outcomeId);
  });
  return url + outcomeIds.join(' | ');
};

const CaesarsParlayLink = (bets: BetSlipItem[]) => {
  if (bets?.length === 0 || bets?.filter(bet => bet.deepLinkUrl?.includes('?selectionIds=')).length === 0) {
    return null;
  }
  const filteredBets = bets.filter(bet => bet.deepLinkUrl?.includes('?selectionIds='));
  let url = filteredBets[0].deepLinkUrl?.split('?selectionIds=')[0] + '?selectionIds=';
  const outcomeIds: string[] = [];
  filteredBets.forEach((bet) => {
    let firstParse = bet.deepLinkUrl?.split('?selectionIds=')[1];
    let outcomeId = firstParse?.split('&')[0];
    if (outcomeId) outcomeIds.push(outcomeId);
  });
  return url + outcomeIds.join(',');
};

const BetMGMParlayLink = (bets: BetSlipItem[]): string | null => {
  const urls = bets.map(b => b.deepLinkUrl).filter(u => u?.includes("?options=")) as string[];
  if (!urls.length) return null;
  const base = urls[0].split("?options=")[0] + "?options=";

  const regOutcomes: string[] = [];
  const sgpOrder: string[] = [];
  const sgpByEvent = new Map<string, { s: string; e: string; m: string; x: string }[]>();

  for (const p of urls.map(u => u.split("?options=")[1]!.split("&")[0]).filter(Boolean)) {
    for (const t of p.split(",").flatMap(z => z.split("~")).map(z => z.trim()).filter(Boolean)) {
      if (/^\d+:\d+-\d+-\d+$/.test(t)) {
        const [s, rest] = t.split(":");
        const [e, m, x] = rest.split("-");
        const k = `${s}:${e}`;
        if (!sgpByEvent.has(k)) { sgpByEvent.set(k, []); sgpOrder.push(k); }
        sgpByEvent.get(k)!.push({ s, e, m, x });
      } else {
        regOutcomes.push(t);
      }
    }
  }
  if (!regOutcomes.length && !sgpOrder.length) return null;

  let gid = 1;
  const sgpOutcomes = sgpOrder.map(k =>
    sgpByEvent.get(k)!.map(l => `${l.s}:${l.e}_${gid}_${l.m}_${l.x}_false`).join("~") && gid++
  ).map((_, i) =>
    sgpByEvent.get(sgpOrder[i])!.map(l => `${l.s}:${l.e}_${i + 1}_${l.m}_${l.x}_false`).join("~")
  );

  return base + [...regOutcomes, ...sgpOutcomes].join(",");
};

const KambiParlayLink = (bets: BetSlipItem[]) => {
  if (!bets?.length || bets.filter(bet => bet.deepLinkUrl?.includes('?coupon=single')).length === 0) {
    return null;
  }
  const filteredBets = bets.filter(bet => bet.deepLinkUrl?.includes('?coupon=single'));
  let url = filteredBets[0].deepLinkUrl?.split('?coupon=single')[0] + '?coupon=combination';
  const outcomeIds: string[] = [];
  filteredBets.forEach((bet) => {
    let outcomeId = bet.deepLinkUrl?.split('|')[1];
    if (outcomeId) outcomeIds.push(outcomeId);
  });
  return url + '|' + outcomeIds.join(',') + '|0|replace';
};

const Bet365ParlayLink = (bets: BetSlipItem[]) => {
  if (!bets?.length || bets.filter(bet => bet.deepLinkUrl?.includes('?bs=')).length === 0) {
    return null;
  }
  const filteredBets = bets.filter(bet => bet.deepLinkUrl?.includes('?bs='));
  let url = filteredBets[0].deepLinkUrl?.split('?bs=')[0] + '?bs=';
  const outcomeIds: string[] = [];
  filteredBets.forEach((bet) => {
    let firstParse = bet.deepLinkUrl?.split('?bs=')[1];
    let outcomeId = firstParse?.split('&')[0];
    if (outcomeId) outcomeIds.push(outcomeId);
  });
  return url + outcomeIds.join('|') + '&bet=1';
};

/**
 * Groups bets by sportsbook and returns deep links for each group
 */
export const getBetSlipDeepLinks = (bets: BetSlipItem[], isMobile: boolean = false): { book: string; url: string; bets: BetSlipItem[] }[] => {
  if (!bets?.length) {
    return [];
  }

  // Group bets by sportsbook
  const betsByBook: Record<string, BetSlipItem[]> = {};
  for (const bet of bets) {
    const book = bet.book || 'Unknown';
    if (!betsByBook[book]) {
      betsByBook[book] = [];
    }
    betsByBook[book].push(bet);
  }

  // Generate deep link for each book's bets
  const result: { book: string; url: string; bets: BetSlipItem[] }[] = [];
  for (const [book, bookBets] of Object.entries(betsByBook)) {
    const url = getPlatformDeepLink(bookBets, isMobile);
    console.log(`[DeepLink] ${book}: ${url ? 'Generated' : 'Failed'}`, {
      book,
      betCount: bookBets.length,
      url: url?.substring(0, 100),
      firstBetUrl: bookBets[0]?.deepLinkUrl?.substring(0, 100)
    });
    if (url) {
      result.push({ book, url, bets: bookBets });
    }
  }

  return result;
};

/**
 * Opens deep links for all bets in the bet slip, grouped by sportsbook
 * Opens the first window immediately (user action), then opens remaining windows with delays
 * Auto-detects actual mobile device (ignores viewport-based isMobile parameter)
 * Returns the deep links for UI fallback if popups are blocked
 */
export const openBetSlipDeepLinks = (bets: BetSlipItem[]): { book: string; url: string; bets: BetSlipItem[] }[] => {
  // Use actual device detection, not viewport-based isMobile
  const isMobileDevice = isActualMobileDevice();
  const deepLinks = getBetSlipDeepLinks(bets, isMobileDevice);

  console.log(`[DeepLink] Platform: ${isMobileDevice ? 'Mobile' : 'Desktop'}, Opening ${deepLinks.length} deep links`, deepLinks.map(d => d.book));

  if (deepLinks.length === 0) {
    return [];
  }

  // Open first link immediately (this is the user-initiated action, most likely to succeed)
  const firstLink = deepLinks[0];
  if (firstLink?.url) {
    console.log(`[DeepLink] Opening ${firstLink.book}:`, firstLink.url.substring(0, 100));
    window.open(firstLink.url, '_blank');
  }

  // For remaining links, we need to open them with delays
  // This gives the browser time to process each popup request
  // Note: Some browsers may still block these, but staggering helps
  const remainingLinks = deepLinks.slice(1);
  remainingLinks.forEach(({ book, url }, index) => {
    if (url) {
      // Stagger opens by 300ms each to avoid rapid-fire popup blocking
      setTimeout(() => {
        console.log(`[DeepLink] Opening ${book} (delayed):`, url.substring(0, 100));
        const newWindow = window.open(url, '_blank');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          console.warn(`[DeepLink] Popup blocked for ${book}. User may need to allow popups.`);
        }
      }, (index + 1) * 300);
    }
  });

  // Return all links so the UI can provide fallback click buttons if needed
  return deepLinks;
};
